/**
 * imageOptimizer — downscale + recompress photos so they stay light.
 *
 * Done at INGEST (when a memory photo is added) so IndexedDB stays small, the
 * app loads fast, and cloud backups are naturally shareable. The phone's gallery
 * keeps the true original — the app only ever holds an optimized copy.
 *
 * Format: WebP when the browser can ENCODE it (Android/Chrome → smaller files),
 * JPEG as fallback (iOS Safari can't encode WebP via canvas and would silently
 * return a huge PNG). Both decode everywhere modern, so restore is portable.
 */

const MAX_SIZE = 1600;   // longest side, px
const QUALITY = 0.82;

function drawToCanvas(img, maxSize) {
  let { width, height } = img;
  const scale = Math.min(1, maxSize / Math.max(width, height));
  width = Math.max(1, Math.round(width * scale));
  height = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function encode(canvas, quality) {
  // Prefer WebP; if the browser doesn't actually encode it, the data URL won't
  // start with data:image/webp (Safari returns PNG) → fall back to JPEG.
  let out = canvas.toDataURL('image/webp', quality);
  if (!out.startsWith('data:image/webp')) {
    out = canvas.toDataURL('image/jpeg', quality);
  }
  return out;
}

function fromUrl(url, maxSize, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try { resolve(encode(drawToCanvas(img, maxSize), quality)); }
      catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = url;
  });
}

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/** Optimize a picked/captured File → optimized data URL. Falls back to the
 *  untouched original if anything goes wrong. */
export async function optimizeImageFile(file, { maxSize = MAX_SIZE, quality = QUALITY } = {}) {
  try {
    const url = URL.createObjectURL(file);
    try { return await fromUrl(url, maxSize, quality); }
    finally { URL.revokeObjectURL(url); }
  } catch {
    try { return await fileToDataURL(file); } catch { return null; }
  }
}

const APPROX_RAW_BYTES = (src) =>
  typeof src === 'string' ? Math.floor(src.length * 0.75)
  : (src && typeof src.size === 'number' ? src.size : 0);

/** Optimize an existing photo source (a Blob or a data: URL string) → data URL.
 *  Used as a backup-time safety net for legacy full-size photos. Only touches
 *  sources above `minBytes` so already-small/optimized photos aren't degraded. */
export async function optimizeSourceToDataUrl(src, { maxSize = MAX_SIZE, quality = QUALITY, minBytes = 400 * 1024 } = {}) {
  try {
    const tooSmallToBother = APPROX_RAW_BYTES(src) > 0 && APPROX_RAW_BYTES(src) < minBytes;
    let url, revoke = false;
    if (typeof src === 'string') {
      if (!src.startsWith('data:image')) return src;
      url = src;
    } else if (src && typeof src.size === 'number') {
      url = URL.createObjectURL(src);
      revoke = true;
    } else {
      return src;
    }
    try {
      if (tooSmallToBother) {
        // Keep as-is but normalize to a data URL (callers expect a string).
        return typeof src === 'string' ? src : await blobToDataURL(src);
      }
      return await fromUrl(url, maxSize, quality);
    } finally {
      if (revoke) URL.revokeObjectURL(url);
    }
  } catch {
    return typeof src === 'string' ? src : await blobToDataURL(src).catch(() => null);
  }
}

const blobToDataURL = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(blob);
});
